import React from 'react';
import { PillNavItem, HeaderActionItem } from '../../../components/layout/InnerLayout';
import { Organization, Project } from '../../../types/domain';
import { OrgTabKey, OrgSettingsTabKey } from '../types';
import { useOrganizationData } from '../useOrganizationData';
import {
  Crown,
  Plus,
  UserPlus,
  Building2,
  Trash2,
  BarChart3,
  FolderKanban,
  Users,
  Settings,
} from 'lucide-react';

interface UseOrgHeaderNavParams {
  activeTab: OrgTabKey;
  settingsTab: OrgSettingsTabKey;
  setSettingsTab: (tab: OrgSettingsTabKey) => void;
  currentOrg: Organization | null;
  currentOrgId: string;
  projects: Project[];
  data: ReturnType<typeof useOrganizationData>;
}

export const useOrganizationHeaderNav = ({
  activeTab,
  settingsTab,
  setSettingsTab,
  currentOrg,
  currentOrgId,
  projects,
  data,
}: UseOrgHeaderNavParams) => {
  let headerTitle: React.ReactNode = currentOrg?.name || 'Organization';
  let headerDescription: React.ReactNode = undefined;
  let headerActions: HeaderActionItem[] | undefined = undefined;
  let navItems: PillNavItem[] | undefined = undefined;

  if (activeTab === 'overview') {
    headerTitle = (
      <>
        <span>{currentOrg?.name || 'Organization'}</span>
        <span className="px-2 py-0.5 rounded-md bg-cf-surfaceActive border border-cf-border text-cf-textSecondary uppercase font-bold text-xs tracking-wider flex items-center gap-1.5 shadow-xs font-normal">
          <Crown size={12} className="text-cf-textSecondary" />
          <span>{currentOrg?.plan_tier ? currentOrg.plan_tier.toUpperCase() : 'MAX'}</span>
        </span>
      </>
    );
    headerDescription = `High-throughput ingestion workspace, quota limits, and datastores for ${currentOrg?.name || 'this organization'}.`;
  } else if (activeTab === 'projects') {
    headerTitle = `Projects (${projects.length})`;
    headerDescription = `Independent datastore workspaces and ingestion endpoints under ${currentOrg?.name}.`;
    if (data.canManageOrg) {
      headerActions = [
        {
          label: 'New Project',
          icon: Plus,
          variant: 'primary',
          onClick: () => data.setCreateProjectOpen(true),
        },
      ];
    }
  } else if (activeTab === 'team') {
    headerTitle = 'Team Members & Invitations';
    headerDescription = `Manage seats, assign roles, and invite teammates to collaborate in ${currentOrg?.name}.`;
    if (data.canManageOrg) {
      headerActions = [
        {
          label: 'Invite Teammate',
          icon: UserPlus,
          variant: 'primary',
          onClick: () => {
            data.setGeneratedInviteUrl(null);
            data.setInviteEmail('');
            data.setInviteModalOpen(true);
          },
        },
      ];
    }
  } else if (activeTab === 'settings' && data.canManageOrg) {
    headerTitle = 'Organization Settings';
    headerDescription = `Manage organization identity, subscription quota limits, and security configuration for ${currentOrg?.name}.`;
    navItems = [
      {
        id: 'general',
        label: 'General',
        icon: Building2,
        active: settingsTab === 'general',
        onClick: () => setSettingsTab('general'),
      },
      {
        id: 'plan',
        label: 'Plan & Quotas',
        icon: Crown,
        active: settingsTab === 'plan',
        onClick: () => setSettingsTab('plan'),
      },
      {
        id: 'danger',
        label: 'Danger Zone',
        icon: Trash2,
        danger: true,
        active: settingsTab === 'danger',
        onClick: () => setSettingsTab('danger'),
      },
    ];
  }

  if (activeTab !== 'settings') {
    navItems = [
      {
        id: 'overview',
        label: 'Overview',
        icon: BarChart3,
        active: activeTab === 'overview',
        href: `/organization/${currentOrgId}`,
        className: 'md:hidden',
      },
      {
        id: 'projects',
        label: `Projects (${projects.length})`,
        icon: FolderKanban,
        active: activeTab === 'projects',
        href: `/organization/${currentOrgId}/projects`,
        className: 'md:hidden',
      },
      {
        id: 'team',
        label: 'Team',
        icon: Users,
        active: activeTab === 'team',
        href: `/organization/${currentOrgId}/team`,
        className: 'md:hidden',
      },
      ...(data.canManageOrg
        ? [
            {
              id: 'settings',
              label: 'Settings',
              icon: Settings,
              active: false,
              href: `/organization/${currentOrgId}/settings`,
              className: 'md:hidden',
            },
          ]
        : []),
    ];
  }

  return {
    headerTitle,
    headerDescription,
    headerActions,
    navItems,
  };
};
